/**
 * ------------------------------------------------------------------
 * one-per-order.js
 * ------------------------------------------------------------------
 * Begrenzt konfigurierte Produkte auf genau 1 Stueck pro Warenkorb
 * (= pro Bestellung).
 *
 * Konfiguration : snippets/one-per-order.liquid  (Produkt-IDs)
 * Datenuebergabe: snippets/js-variables.liquid   (theme.onePerOrder)
 *
 * Warum ueberhaupt noetig: das Add-to-Cart-Formular postet an
 * /cart/add, und /cart/add ADDIERT die Menge auf eine bestehende
 * Zeile. Mehrfaches Klicken erhoeht damit die Menge, obwohl im
 * Warenkorb max="1" gesetzt ist.
 *
 * Ebene 1a  Sobald das Produkt im Warenkorb liegt, werden weitere
 *           Add-to-Cart-Submits abgefangen (kein zweiter Request).
 * Ebene 1b  Sicherheitsnetz: jede Gesamtmenge > 1 wird serverseitig
 *           via /cart/change auf 1 zurueckgesetzt - auch bei
 *           URL-Manipulation, mehreren Tabs oder Alt-Warenkoerben.
 * Ebene 2   Button-Label und Hinweis, solange das Produkt im
 *           Warenkorb liegt.
 *
 * Hinweis: reine Frontend-Absicherung. Manipulationssicher wird die
 * Regel erst mit einer Cart-and-Checkout-Validation Function oder
 * einer Order-Limit-App (siehe Konzeptdokument).
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var config = window.theme && window.theme.onePerOrder;
  if (!config || !config.productIds || !config.productIds.length) return;

  var PRODUCT_IDS = config.productIds.map(Number);
  var STRINGS = config.strings || {};

  var VARIANT_PRODUCT = {}; // variantId -> productId
  var LINES = [];           // [{ key, variantId, productId, quantity }]
  var busy = false;

  Object.keys(config.variants || {}).forEach(function (variantId) {
    VARIANT_PRODUCT[String(variantId)] = Number(config.variants[variantId]);
  });

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  function isLimitedProduct(productId) {
    return PRODUCT_IDS.indexOf(Number(productId)) !== -1;
  }

  function setLines(lines) {
    LINES = lines || [];
    LINES.forEach(function (line) {
      VARIANT_PRODUCT[String(line.variantId)] = Number(line.productId);
    });
  }

  function linesFromCart(cart) {
    if (!cart || !cart.items) return null;
    return cart.items
      .filter(function (item) { return isLimitedProduct(item.product_id); })
      .map(function (item) {
        return {
          key: item.key,
          variantId: Number(item.variant_id || item.id),
          productId: Number(item.product_id),
          quantity: Number(item.quantity)
        };
      });
  }

  function quantityOfProduct(productId) {
    var id = Number(productId);
    return LINES.reduce(function (sum, line) {
      return line.productId === id ? sum + line.quantity : sum;
    }, 0);
  }

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections: sections }
    }));
    return sections;
  }

  function publishCart(cart) {
    if (!window.theme || !window.theme.pubsub) return;
    window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
      source: 'one-per-order',
      cart: cart
    });
  }

  /**
   * Ebene 1b - Gesamtmenge je limitiertem Produkt auf 1 zurueckfahren.
   * Erste Zeile behaelt Menge 1, weitere Zeilen (andere Variante des
   * gleichen Produkts) werden auf 0 gesetzt.
   */
  function enforce() {
    if (busy) return Promise.resolve(false);

    var fixes = [];
    PRODUCT_IDS.forEach(function (productId) {
      var lines = LINES.filter(function (line) { return line.productId === productId; });
      if (!lines.length) return;

      var total = lines.reduce(function (sum, line) { return sum + line.quantity; }, 0);
      if (total <= 1) return;

      lines.forEach(function (line, index) {
        var target = index === 0 ? 1 : 0;
        if (line.quantity !== target) fixes.push({ id: line.key, quantity: target });
      });
    });

    if (!fixes.length) return Promise.resolve(false);

    busy = true;
    var sections = bundledSections();

    return fixes
      .reduce(function (chain, fix) {
        return chain.then(function () {
          return fetch(routes().cart_change_url || '/cart/change', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: fix.id, quantity: fix.quantity, sections: sections })
          }).then(function (response) { return response.json(); });
        });
      }, Promise.resolve(null))
      .then(function (cart) {
        var lines = linesFromCart(cart);
        if (lines) setLines(lines);
        publishCart(cart);
        applyButtonStates();
        return true;
      })
      .catch(function (error) {
        console.error('[one-per-order] Menge konnte nicht korrigiert werden:', error);
        return false;
      })
      .finally(function () { busy = false; });
  }

  function refreshFromServer() {
    var cartUrl = routes().cart_url || '/cart';
    return fetch(cartUrl + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var lines = linesFromCart(cart);
        if (lines) setLines(lines);
        applyButtonStates();
        return enforce();
      })
      .catch(function () { /* offline o.ae. - stiller Fallback */ });
  }

  /* ---------------------------------------------------------------- */
  /* Ebene 2 - Button-Zustand                                         */
  /* ---------------------------------------------------------------- */

  function submitButtonsOf(form) {
    var buttons = Array.prototype.slice.call(form.querySelectorAll('[type="submit"]'));
    if (form.id) {
      var escaped = window.CSS && CSS.escape ? CSS.escape(form.id) : form.id;
      Array.prototype.slice
        .call(document.querySelectorAll('[type="submit"][form="' + escaped + '"]'))
        .forEach(function (button) {
          if (buttons.indexOf(button) === -1) buttons.push(button);
        });
    }
    return buttons;
  }

  function labelOf(button) {
    return button.querySelector('.btn-text > span') || button.querySelector('.btn-text') || button;
  }

  function setButtonState(button, active) {
    var label = labelOf(button);

    if (active) {
      if (!button.hasAttribute('data-one-per-order-active')) {
        button.setAttribute('data-one-per-order-original', label.textContent);
        button.setAttribute('data-one-per-order-active', '');
      }
      if (STRINGS.alreadyInCart) label.textContent = STRINGS.alreadyInCart;
      return;
    }

    if (button.hasAttribute('data-one-per-order-active')) {
      label.textContent = button.getAttribute('data-one-per-order-original') || label.textContent;
      button.removeAttribute('data-one-per-order-original');
      button.removeAttribute('data-one-per-order-active');
    }
  }

  function limitedForms() {
    return Array.prototype.slice
      .call(document.querySelectorAll('form[action*="/cart/add"]'))
      .map(function (form) {
        var input = form.querySelector('[name="id"]');
        if (!input || !input.value) return null;
        var productId = VARIANT_PRODUCT[String(input.value)];
        if (!productId) return null;
        return { form: form, productId: productId };
      })
      .filter(Boolean);
  }

  function applyButtonStates() {
    limitedForms().forEach(function (entry) {
      var active = quantityOfProduct(entry.productId) >= 1;
      submitButtonsOf(entry.form).forEach(function (button) {
        setButtonState(button, active);
      });
      if (!active) hideNotice(entry.form);
    });
  }

  function showNotice(form) {
    if (!STRINGS.notice) return;

    var host = form.querySelector('.product-form__buttons');
    if (!host) return; // z.B. Quick-Add-Karten: kein Platz fuer den Hinweis

    var notice = host.querySelector('[data-one-per-order-notice]');
    if (!notice) {
      notice = document.createElement('div');
      notice.setAttribute('data-one-per-order-notice', '');
      notice.setAttribute('role', 'status');
      notice.className = 'alert alert--info text-sm font-medium leading-tight';
      host.appendChild(notice);
    }
    notice.textContent = STRINGS.notice;
    notice.hidden = false;
  }

  function hideNotice(form) {
    var notice = form.querySelector('[data-one-per-order-notice]');
    if (notice) notice.hidden = true;
  }

  /* ---------------------------------------------------------------- */
  /* Ebene 1a - weitere Add-to-Cart-Submits abfangen                  */
  /* ---------------------------------------------------------------- */

  function onSubmitCapture(event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;

    var input = form.querySelector('[name="id"]');
    if (!input || !input.value) return;

    var productId = VARIANT_PRODUCT[String(input.value)];
    if (!productId) return; // unbekannte Variante - Ebene 1b greift

    if (quantityOfProduct(productId) < 1) return; // erster Add: normal durchlassen

    // Produkt liegt bereits im Warenkorb: Theme-Handler nicht ausfuehren
    event.preventDefault();
    event.stopImmediatePropagation();

    showNotice(form);
    applyButtonStates();

    var cartType = (window.theme && window.theme.settings && window.theme.settings.cartType) || '';
    if (cartType !== 'page') {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    }
  }

  /* ---------------------------------------------------------------- */
  /* Init                                                             */
  /* ---------------------------------------------------------------- */

  setLines((config.cartLines || []).map(function (line) {
    return {
      key: line.key,
      variantId: Number(line.variantId),
      productId: Number(line.productId),
      quantity: Number(line.quantity)
    };
  }));

  document.addEventListener('submit', onSubmitCapture, true);

  if (window.theme && window.theme.pubsub) {
    window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || !event.cart) return;
      var lines = linesFromCart(event.cart);
      if (lines) setLines(lines);
      setTimeout(function () {
        applyButtonStates();
        if (event.source !== 'one-per-order') enforce();
      }, 0);
    });

    window.theme.pubsub.subscribe(window.theme.pubsub.PUB_SUB_EVENTS.variantChange, function () {
      setTimeout(applyButtonStates, 0);
    });
  }

  document.addEventListener('cart:updated', function () {
    setTimeout(applyButtonStates, 0);
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshFromServer();
  });

  applyButtonStates();
  enforce();
})();
